/**
 * LifeAdvice Analytics — the five remaining analytics endpoints on one page.
 *
 * The shared TimeRangeFilter drives a single `days` window, which is what every
 * /analytics/* route accepts (clamped 1–730 server-side). Charts are recharts,
 * coloured from the API's own category hex values where they exist and from
 * brandColors otherwise; the funnel uses the four discrete band colours in
 * theme/brandColors.ts.
 */

import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Stack,
  LinearProgress,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

import EmptyState from '../../common/EmptyState';
import TimeRangeFilter from '../../common/TimeRangeFilter';
import useTimeRangeParam from '../../common/useTimeRangeParam';
import { rangeDays, formatCustomLabel, toYmd } from '../../common/timeRange';
import { brandColors } from '../../../theme/brandColors';
import {
  lifeAdviceApi,
  EntriesOverTimePoint,
  CategoryDistributionRow,
  ShareMethodRow,
  TopPromptRow,
  FunnelResult,
} from '../../../services/lifeAdviceAdminService';
import { PageHeader, errorMessage, isBackendDown, formatDate } from '../common/lifeAdviceUi';

/** Funnel band colours, widest → narrowest (brandColors.ts activation bands). */
const FUNNEL_BANDS = [
  brandColors.bandRetained,
  brandColors.bandMedium,
  brandColors.bandLow,
  brandColors.deepTealLight,
  brandColors.bandCold,
];

const SHARE_METHOD_COLORS = [
  brandColors.deepTeal,
  brandColors.warmGold,
  brandColors.forestGreen,
  brandColors.richBurgundy,
  brandColors.slateGray,
];

const LifeAdviceAnalyticsPage: React.FC = () => {
  const [period, setPeriod] = useTimeRangeParam({ type: '30d' });

  const [timeseries, setTimeseries] = React.useState<EntriesOverTimePoint[]>([]);
  const [distribution, setDistribution] = React.useState<CategoryDistributionRow[]>([]);
  const [shareMethods, setShareMethods] = React.useState<ShareMethodRow[]>([]);
  const [topPrompts, setTopPrompts] = React.useState<TopPromptRow[]>([]);
  const [funnel, setFunnel] = React.useState<FunnelResult | null>(null);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [down, setDown] = React.useState(false);

  // Every /analytics/* route here takes `days=N` and counts BACK FROM NOW —
  // there is no from/to on the wire. An inclusive span is therefore translated
  // to its length, and a custom window that ends in the past is reported
  // honestly below rather than silently re-anchored to today. Same disclosure
  // as RevenueSubscriptionsPage, which shares the dialect.
  const days = rangeDays(period);

  /** True when the operator picked a custom span that does not end today. */
  const customEndsInPast = period.type === 'custom' && period.to < toYmd(new Date());

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    setDown(false);
    try {
      const [t, d, s, p, f] = await Promise.all([
        lifeAdviceApi.analytics.entriesOverTime(days),
        lifeAdviceApi.analytics.categoryDistribution(days),
        lifeAdviceApi.analytics.shareMethods(days),
        lifeAdviceApi.analytics.topPrompts(days, 10),
        lifeAdviceApi.analytics.funnel(days),
      ]);
      setTimeseries(t);
      setDistribution(d);
      setShareMethods(s);
      setTopPrompts(p);
      setFunnel(f);
    } catch (e) {
      setDown(isBackendDown(e));
      setError(errorMessage(e, 'Failed to load LifeAdvice analytics.'));
    } finally {
      setLoading(false);
    }
  }, [days]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  if (loading && !funnel) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (down) {
    return (
      <EmptyState
        icon="error"
        title="LifeAdvice service is unreachable"
        description={`The admin API could not reach lifescribe-advice. ${error || ''}`}
        action={{ label: 'Retry', onClick: reload, variant: 'contained' }}
      />
    );
  }

  const pillarRows = distribution.filter((d) => d.entry_count > 0);
  const promptRows = topPrompts.filter((p) => p.entry_count > 0 || p.usage_count > 0).slice(0, 10);
  const started = funnel?.stages?.[0]?.count ?? 0;
  const hasAnything =
    started > 0 || pillarRows.length > 0 || shareMethods.length > 0 || promptRows.length > 0;

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={<InsightsIcon sx={{ fontSize: 32, color: brandColors.deepTeal }} />}
        title="LifeAdvice Analytics"
        subtitle="Creation trends, pillar mix, how advice gets sent, and where members drop off."
        helpId="lifeadvice-analytics"
        actions={
          <Stack spacing={1} alignItems="flex-end">
            <TimeRangeFilter
              value={period}
              onChange={setPeriod}
              onRefresh={reload}
              refreshing={loading}
            />

            {/* Say what the numbers actually cover. Every endpoint here counts
                back from now, so a window ending in the past cannot be
                honoured — printing the picked dates would be a lie about the
                data. */}
            {customEndsInPast && (
              <Typography
                sx={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '11.5px',
                  color: 'text.secondary',
                  maxWidth: 420,
                  textAlign: 'right',
                  lineHeight: 1.4,
                }}
              >
                These figures measure back from today. {formatCustomLabel(period)} is
                {' '}{days} days long, so the last {days} days — ending today — are
                what is shown.
              </Typography>
            )}
          </Stack>
        }
      />

      {error && !down && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!loading && !hasAnything && !error ? (
        <EmptyState
          icon="empty_box"
          title="No LifeAdvice activity in this window"
          description="Widen the time period, or check back once members start recording advice."
          action={{ label: 'Refresh', onClick: reload, variant: 'outlined' }}
        />
      ) : (
        <Grid container spacing={2}>
          {/* Entries over time */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Paper sx={{ p: 2.5, borderRadius: '14px', height: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                Entries created vs shared
              </Typography>
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={timeseries} margin={{ top: 4, right: 8, bottom: 4, left: -16 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="day"
                      tickFormatter={(v) => formatDate(String(v))}
                      tick={{ fontSize: 11 }}
                      minTickGap={24}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <RTooltip labelFormatter={(v) => formatDate(String(v))} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line
                      type="monotone"
                      dataKey="entries_created"
                      name="Created"
                      stroke={brandColors.deepTeal}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="entries_shared"
                      name="Shared"
                      stroke={brandColors.warmGold}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Paper>
          </Grid>

          {/* Category donut */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Paper sx={{ p: 2.5, borderRadius: '14px', height: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                Entries by wisdom pillar
              </Typography>
              {pillarRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No entries in this window.
                </Typography>
              ) : (
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pillarRows}
                        dataKey="entry_count"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={2}
                      >
                        {pillarRows.map((row) => (
                          <Cell key={row.category_id} fill={row.color || brandColors.slateGray} />
                        ))}
                      </Pie>
                      <RTooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Paper>
          </Grid>

          {/* Share methods */}
          <Grid size={{ xs: 12, lg: 5 }}>
            <Paper sx={{ p: 2.5, borderRadius: '14px', height: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                How advice gets sent
              </Typography>
              {shareMethods.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nothing shared in this window.
                </Typography>
              ) : (
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={shareMethods} margin={{ top: 4, right: 8, bottom: 4, left: -16 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="share_method" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <RTooltip />
                      <Bar dataKey="share_count" name="Shares" radius={[6, 6, 0, 0]}>
                        {shareMethods.map((row, i) => (
                          <Cell
                            key={row.share_method}
                            fill={SHARE_METHOD_COLORS[i % SHARE_METHOD_COLORS.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Paper>
          </Grid>

          {/* Top prompts (horizontal) */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <Paper sx={{ p: 2.5, borderRadius: '14px', height: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                Top prompts
              </Typography>
              {promptRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No prompt usage in this window.
                </Typography>
              ) : (
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={promptRows}
                      margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                      <YAxis
                        type="category"
                        dataKey="title"
                        width={190}
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v) =>
                          String(v).length > 34 ? `${String(v).slice(0, 33)}…` : String(v)
                        }
                      />
                      <RTooltip />
                      <Bar dataKey="entry_count" name="Entries" radius={[0, 6, 6, 0]}>
                        {promptRows.map((row) => (
                          <Cell key={row.prompt_id} fill={row.color || brandColors.deepTeal} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Paper>
          </Grid>

          {/* Funnel */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2.5, borderRadius: '14px' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Creation funnel
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Started → captured → AI transformed → voice cloned → shared, over the last{' '}
                {funnel?.windowDays ?? days} days.
              </Typography>
              {!funnel || started === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No entries started in this window.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {funnel.stages.map((stage, i) => (
                    <Box key={stage.stage}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {stage.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {stage.count.toLocaleString()} · {stage.percent}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, Math.max(0, stage.percent))}
                        aria-label={`${stage.label}: ${stage.percent}%`}
                        sx={{
                          height: 14,
                          borderRadius: 7,
                          backgroundColor: 'action.hover',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 7,
                            backgroundColor: FUNNEL_BANDS[i % FUNNEL_BANDS.length],
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default LifeAdviceAnalyticsPage;
